import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Shield, Users, UserCircle, Phone, Instagram, Languages,
  Trash2, Loader2, Bell, Store, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api, getMe, logout } from "../lib/api";
import { Switch } from "../components/ui/switch";
import { useI18n } from "../lib/i18n";
import { BottomNav } from "../components/BottomNav";

// Three audiences, three sections. Toggles default to ON for every audience —
// the user can opt-out per field. Anonymous mode is a separate user-wide
// override that hides everything from businesses + vendors instantly.

const BIZ_FIELDS = [
  ["biz_share_name",      "Display name"],
  ["biz_share_photo",     "Profile photo"],
  ["biz_share_email",     "Email address"],
  ["biz_share_language",  "Language preference"],
  ["biz_share_instagram", "Instagram handle"],
];

const VENDOR_FIELDS = [
  ["vendor_share_name",      "Display name"],
  ["vendor_share_photo",     "Profile photo"],
  ["vendor_share_email",     "Email address"],
  ["vendor_share_language",  "Language preference"],
  ["vendor_share_instagram", "Instagram handle"],
  ["vendor_share_whatsapp",  "Share my WhatsApp with vendors I interact with"],
];

const USER_FIELDS = [
  ["user_share_name",      "Display name"],
  ["user_share_photo",     "Profile photo"],
  ["user_share_email",     "Email address"],
  ["user_share_language",  "Language preference"],
  ["user_share_instagram", "Instagram handle"],
  ["user_share_whatsapp",  "WhatsApp"],
];

export default function PrivacySettings() {
  const navigate = useNavigate();
  const { lang, setLang } = useI18n();
  const [user, setUser] = useState(null);
  const [p, setP] = useState({});
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    getMe().then((u) => {
      if (!u) return navigate("/");
      setUser(u);
      setP(u.privacy_settings || {});
    });
  }, [navigate]);

  // Apply patch optimistically; persist via PATCH /privacy/settings.
  const save = async (patch) => {
    const next = { ...p, ...patch };
    setP(next);
    try {
      await api.patch("/privacy/settings", { patch });
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const saveProfile = async (patch) => {
    try {
      const { data } = await api.patch("/users/me", patch);
      setUser(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const del = async () => {
    if (!window.confirm("Delete your Habano account? This sets deleted_at and signs you out.")) return;
    if (!window.confirm("Are you sure? This action is irreversible.")) return;
    setDeleting(true);
    try {
      await api.delete("/users/me");
      toast.success("Account deleted");
      await logout();
      navigate("/");
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
      setDeleting(false);
    }
  };

  // Anonymous mode is a master switch — always visible at top.
  const setAnon = (v) => save({ anonymous_mode: !!v });

  if (!user) return null;
  return (
    <main data-testid="privacy-page" className="min-h-screen pb-32 px-5 bg-background pt-6">
      <header className="flex items-center gap-3">
        <Link to="/settings" className="btn-ghost p-2"><ArrowLeft className="w-4 h-4" strokeWidth={1.5}/></Link>
        <div>
          <div className="eyebrow">Settings</div>
          <h1 className="font-display text-3xl mt-1 text-foreground">Privacy</h1>
        </div>
      </header>

      {/* ANONYMOUS MASTER SWITCH */}
      <Section icon={Shield} title="Anonymous mode" testid="section-anonymous">
        <Toggle tid="priv-anonymous_mode"
                label="Hide me from businesses and vendors"
                checked={!!p.anonymous_mode}
                onChange={setAnon} />
        {p.anonymous_mode && (
          <div className="mt-2 text-[12px] text-gold" style={{ fontFamily: "'DM Sans', sans-serif" }} data-testid="anon-banner">
            Businesses and vendors see you as <strong>Anonymous Customer #{anonNum(user.user_id)}</strong>.
            Your friends still see your name per the friends settings below.
          </div>
        )}
      </Section>

      {/* SECTION 1 — BUSINESSES */}
      <Section icon={Store} title="Share with businesses" testid="section-businesses">
        {BIZ_FIELDS.map(([k, label]) => (
          <Toggle key={k} tid={`priv-${k}`} label={label}
                  checked={p[k] !== false} onChange={(v) => save({ [k]: v })}
                  disabled={!!p.anonymous_mode} />
        ))}
        <div className="mt-3 rounded-md p-3 flex gap-2"
             style={{ background: "rgba(232,184,75,0.08)", border: "1px solid rgba(232,184,75,0.3)" }}>
          <AlertCircle className="w-4 h-4 text-gold shrink-0 mt-0.5" strokeWidth={1.5} />
          <p className="text-[12px] text-foreground/85" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Businesses never see your WhatsApp number or spending records regardless of your settings.
          </p>
        </div>
      </Section>

      {/* SECTION 2 — VENDORS */}
      <Section icon={UserCircle} title="Share with vendors" testid="section-vendors">
        {VENDOR_FIELDS.map(([k, label]) => (
          <Toggle key={k} tid={`priv-${k}`} label={label}
                  checked={p[k] !== false} onChange={(v) => save({ [k]: v })}
                  disabled={!!p.anonymous_mode} />
        ))}
        <p className="mt-3 text-[12px] text-foreground/55" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          Vendors are independent guides and drivers who may need to contact you directly to coordinate meetups.
        </p>
      </Section>

      {/* SECTION 3 — OTHER USERS */}
      <Section icon={Users} title="Share with other users" testid="section-users">
        <p className="text-[12px] text-foreground/55 mb-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          These settings apply to all users by default.
        </p>
        {USER_FIELDS.map(([k, label]) => (
          <Toggle key={k} tid={`priv-${k}`} label={label}
                  checked={p[k] !== false} onChange={(v) => save({ [k]: v })} />
        ))}
        <div className="mt-3">
          <Link to="/friends" data-testid="priv-friend-overrides-link" className="inline-flex items-center gap-1.5 text-gold hover:opacity-80 transition-opacity"
                style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13 }}>
            Manage per-friend overrides →
          </Link>
        </div>
        <Toggle tid="priv-notify_friend_checkin_at_saved"
                label="Notify me when friends check in at my saved places"
                checked={p.notify_friend_checkin_at_saved !== false}
                onChange={(v) => save({ notify_friend_checkin_at_saved: v })} />
        <p className="text-[11px] text-foreground/55 mt-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          You will be notified when a friend checks in at a business you have both saved.
        </p>
      </Section>

      <Section icon={Bell} title="Notifications">
        <Toggle tid="priv-havana_updates" label="Havana Updates"
                checked={(p.notifications || {}).havana_updates !== false}
                onChange={(v) => save({ notifications: { ...(p.notifications || {}), havana_updates: v } })} />
        <div className="text-[11px] text-foreground/55 mt-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          Receive notifications when important travel updates are published.
        </div>
      </Section>

      <Section icon={Phone} title="Connected accounts">
        <Field label="WhatsApp"><input data-testid="priv-wa" defaultValue={user.whatsapp || ""} onBlur={(e) => e.target.value !== user.whatsapp && saveProfile({ whatsapp: e.target.value })} className="form-input" placeholder="+5355551234" /></Field>
        <Field label="Instagram"><input data-testid="priv-ig" defaultValue={user.instagram || ""} onBlur={(e) => e.target.value !== user.instagram && saveProfile({ instagram: e.target.value })} className="form-input" placeholder="handle" /></Field>
      </Section>

      <Section icon={Languages} title="Language">
        <div className="flex gap-2" data-testid="language-picker">
          {["en", "es"].map((l) => (
            <button key={l} data-testid={`lang-${l}`} onClick={() => { setLang(l); saveProfile({ language: l }); }}
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest border ${lang === l ? "bg-gold text-[hsl(var(--primary-foreground))] border-transparent" : "border-gold-soft text-foreground/65"}`}
              style={{ minHeight: 48 }}>
              {l === "en" ? "English" : "Español"}
            </button>
          ))}
        </div>
      </Section>

      <Section icon={Trash2} title="Account" danger>
        <div className="text-xs text-foreground/55 mb-3">Deleting your account is permanent. Required by App Store compliance.</div>
        <button data-testid="delete-account" onClick={del} disabled={deleting} className="btn-danger py-2 px-4 text-xs">
          {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5}/>}
          Delete my account
        </button>
      </Section>

      <BottomNav />
    </main>
  );
}

function anonNum(uid) {
  // Mirror backend: 4-digit deterministic suffix from user_id
  let h = 0;
  for (let i = 0; i < uid.length; i++) h = (h * 31 + uid.charCodeAt(i)) >>> 0;
  return 1000 + (h % 9000);
}

function Section({ icon: Icon, title, children, danger, testid }) {
  return (
    <section data-testid={testid} className={`mt-6 lux-card p-5 ${danger ? "border-destructive/40" : ""}`}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className={`w-4 h-4 ${danger ? "text-destructive" : "text-gold"}`} strokeWidth={1.5}/>
        <div className="font-display text-xl text-foreground">{title}</div>
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Toggle({ tid, label, checked, onChange, disabled }) {
  return (
    <div className={`flex items-center justify-between gap-3 ${disabled ? "opacity-50" : ""}`} style={{ minHeight: 48 }}>
      <div className="text-sm text-foreground flex-1" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14 }}>{label}</div>
      <Switch data-testid={tid} checked={checked} onCheckedChange={onChange} disabled={disabled} />
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><div className="eyebrow mb-1">{label}</div>{children}</label>;
}
